import { useCallback, useEffect, useState } from 'react';
import { config } from '../../../app-confi/app-config';
import { usePrevious } from '../../../common/react-helpers';
import { Building } from '../../../confi-state';
import { ServiceManager } from '../../../services/service-manager';
import { alignment } from '../models/alignment.enum';
import { ColumnService } from './column-service';
import { CorridorService } from './corridor.service';
import { bodiesEnum } from './models/bodies.enum';
import { columnType } from './models/column-type.enum';
import { headType } from './models/head-type.enum';
import { defaultHead, getHeadTypesModel } from './models/head-types.model';
import { plinthType } from './models/plinth-type.enum';
import { getDefaultPropsCorridor, PropsCorridor } from './models/props-config';
import { FormControl, FormControlLabel, FormGroup, Switch, InputLabel, Select, MenuItem, SelectChangeEvent } from '@mui/material';
import { buildingType } from '../models/building-type.enum';
import { Point3 } from '../../../common/models/point.type';
import { InitialProps } from '../Common/BuildingInitialPropertiesDialog/init-props';
import NumberArrowSelector from '../../Common/Inputs/NumberArrowSelector/NumberArrowSelector';
import buildingConfStyles from '../BuildingConf.module.css';
import { columnDefs, columnSubtype } from './models/column-definition';
import { segmentType } from './models/segment-type';
import { SectionJSON } from '../../../models/section-json';
import { ColDefOption } from '../../../app-confi/services/building-helper.service';

const buildingChange = (buildingProps: PropsCorridor, currentBuilding: Building, onChangeBuilding) => {
    onChangeBuilding(currentBuilding.projectId, currentBuilding.siteId, { ...currentBuilding, props: { ...buildingProps } }, false);
};

const Corridor = ({
    currentBuilding,
    buildingScaleDimensions,
    previousBuildingDimensions,
    initProps,
    sectionJson,
    onChangeBuilding
}: {
    currentBuilding: Building,
    buildingScaleDimensions: Point3,
    previousBuildingDimensions: Point3,
    initProps: InitialProps,
    sectionJson: SectionJSON,
    onChangeBuilding: (projectId: number | string, siteId: number | string, building: Building, saveToDb: boolean) => void
}) => {

    const [buildingProps, setBuildingProps] = useState<PropsCorridor>(
        undefined
    );
    const [buildingLengthUpdatedByColumns, setBuildingLengthUpdatedByColumns] = useState<boolean>(
        false
    );
    const [moosPlazaBays, setMoosPlazaBays] = useState<number>(undefined);
    const previousMoosPlazaBays = usePrevious<number>(moosPlazaBays);
    const [moosPlazaBaysOptions, setMoosPlazaBaysOptions] = useState<{ bays: number, squareMeters: number }[]>([]);
    const [moosPlazaTopOptions, setMoosPlazaTopOptions] = useState<{
        forMoosPlazaBays: number,
        options: ColDefOption<columnType | headType>[]
    }[]>(
        []
    );
    const [headOptions, setHeadOptions] = useState<{ options: ColDefOption<columnType | headType>[] }[]>([
        { options: [] },
    ]);

    const previousBuildingScaleDimensions = usePrevious<Point3>(buildingScaleDimensions);

    const maxLength = 1000;
    const maxHeight = 33;
    const maxWidth = 100;
    const minLength = 40;
    const minWidth = 21;
    const minHeight = 9;


    const recomputeColumns = useCallback((computeAmounts: boolean, bp: PropsCorridor) => {
        let b: PropsCorridor = { ...bp };
        if (computeAmounts) {
            const amounts = CorridorService.getAmountsByAvailableBays(b);
            b = { ...b, ...amounts };
        }

        const columnService = new ColumnService();
        b.parts = columnService.createPossibleColumnDefinitions(
            {
                amount_C_L_Columns: b.amount_C_L_Columns,
                amount_C_S_Columns: b.amount_C_S_Columns,
                amount_C_M_Columns: b.amount_C_M_Columns,
                amount_C_XS_Columns: b.amount_C_XS_Columns,
                amount_C_XL_Columns: b.amount_C_XL_Columns,
                selectedPlinthType: b.plinthType,
                selectedMiddleType: b.middleType,
                selectedCrownType: b.crownType,
                plinthFloors: b.plinthFloors,
                middleFloors: ServiceManager.buildingHelperService.getMiddleFloors(b.plinthType, b.buildingHeight, b.customPlinthHeight, b.crownFloors, b.plinthFloors, buildingType.doubleLoadedCorridor),
                crownFloors: b.crownFloors,
                selectedMoosPlazaType: b.moosPlazaType,
                moosPlazaPosition: b.moosPlazaPosition,
                selectedMoosPlazaAlignment: b.moosPlazaAlignment,
                selectedHeadType: b.selectedHeadType,
                nrOfStaircaseCores: CorridorService.getStaircaseCores(b),
                nrOfElevatorCores: CorridorService.getCoresToOccupyWithElevators(b),
                customPlinthHeight: b.customPlinthHeight,
                customPlinthWidth: b.customPlinthWidth,
            }
        );

        b.parts = ServiceManager.babylonService.corridorBabylonService.mapPartsTo3D(
            b.parts,
            b.moosPlazaAlignment,
            b.moosPlazaPosition,
        );
        b.totalAmountOfAllUnits = columnService.getTotalAmountOfAllUnits(b.parts);

        const finalColumnsArray = b.parts.map(p => p.columns).flat();

        b.info = ServiceManager.buildingHelperService.computeColumnInfo(finalColumnsArray);

        setBuildingProps({ ...b });
        buildingChange({ ...b }, currentBuilding, onChangeBuilding);
    }, [currentBuilding, onChangeBuilding]);

    useEffect(() => {
        if (currentBuilding.props) {
            setBuildingProps({ ...currentBuilding.props as PropsCorridor });
            // migrate
            const columns = (currentBuilding.props as any).columns;
            if (columns !== undefined) {
                delete (currentBuilding.props as any).columns;
                recomputeColumns(true, currentBuilding.props as PropsCorridor);
            }
        }
    }, [currentBuilding.props, recomputeColumns])

    useEffect(() => {
        if (moosPlazaBays) {
            if (previousMoosPlazaBays !== undefined && previousMoosPlazaBays !== moosPlazaBays) {
                const option = moosPlazaTopOptions.find(s => s.forMoosPlazaBays === moosPlazaBays);
                updateMoosPlazaType(option.options[0].type as columnType);
            }
        }
        if (moosPlazaBays === undefined && buildingProps && buildingProps.moosPlazaType !== columnType.none) {
            setMoosPlazaBays(CorridorService.getColumnBays(buildingProps.moosPlazaType));
        }
    }, [moosPlazaBays])

    const getMoosPlazaBayData = (): { bays: number, squareMeters: number }[] => {
        const s = columnDefs.map(s => {
            let bays = 0;
            let squareMeters = 0;
            if (s.columnSubtypes.includes(columnSubtype.moosplaza)) {
                bays = s.bays;
                const plinthDef = s.segmentDefinitions.find(sd => sd.type === segmentType.plinth);
                const sectionType = plinthDef.floorVariants[0].variants[0].sections[0];
                const sectionDescription = sectionJson.sections.find(s => s.sectionType === sectionType);
                if (sectionDescription) {
                    const moosPlazaAmenity = sectionJson.amenities.find(a => a.amenityType === sectionDescription.amenities[0].amenityType);
                    if (moosPlazaAmenity) {
                        squareMeters = moosPlazaAmenity.GFA;
                    }
                }
            }
            return { bays, squareMeters }
        });
        const baySqm = s.filter(s => s.bays !== 0);
        const uniqueBays = [];
        baySqm.forEach(bs => {
            if (!uniqueBays.find(s => s?.bays === bs.bays)) {
                uniqueBays.push(bs);
            }
        })
        return uniqueBays;
    }


    const getMoosPlazaTopOptionsForAvailableBays = (availableBays: number[]): {
        forMoosPlazaBays: number,
        options: ColDefOption<columnType | headType>[]
    }[] => {

        let res = availableBays.map(bay => {
            const moosPlazaColumnsOfBaySize = columnDefs.filter(c => c.columnSubtypes.includes(columnSubtype.moosplaza) && c.bays === bay)
            const s = moosPlazaColumnsOfBaySize.map(colDef => {
                return ServiceManager.buildingHelperService.getColumnOptions(colDef, segmentType.middle, sectionJson);
            });
            return { forMoosPlazaBays: bay, options: s };
        });
        return res;
    };


    const getHeadOptions = () => {
        const headDefinitions = columnDefs.filter(c =>
            c.columnSubtypes.includes(columnSubtype.head));
        const options = headDefinitions.map(colDef => {
            return ServiceManager.buildingHelperService.getColumnOptions(colDef, segmentType.middle, sectionJson);
        });
        return options;
    }

    useEffect(() => {
        if (currentBuilding && !currentBuilding.props) {
            const props = getDefaultPropsCorridor(initProps);
            if (previousBuildingDimensions && (previousBuildingDimensions[0] !== 0 && previousBuildingDimensions[1] !== 0 && previousBuildingDimensions[2] !== 0)) {
                props.buildingLength = ServiceManager.utilityService.keepInBounds(previousBuildingDimensions[0], minLength, maxLength);
                props.buildingHeight = ServiceManager.utilityService.keepInBounds(previousBuildingDimensions[1], minHeight, maxHeight);
            }
            recomputeColumns(true, { ...props });
        } else if (buildingProps && moosPlazaBays === undefined) {
            setMoosPlazaBays(CorridorService.getColumnBays(buildingProps.moosPlazaType));
            const options = getMoosPlazaBayData();
            setMoosPlazaBaysOptions(options);
            const topAvailableOptions = getMoosPlazaTopOptionsForAvailableBays(options.map(o => o.bays));
            setMoosPlazaTopOptions(topAvailableOptions);

            const headOptions = getHeadOptions();
            setHeadOptions([{
                options: headOptions
            }])

        }
    }, [buildingProps, currentBuilding, previousBuildingDimensions, onChangeBuilding, recomputeColumns])

    useEffect(() => {
        if (buildingScaleDimensions && buildingProps && (
            buildingScaleDimensions[0] !== previousBuildingScaleDimensions[0] ||
            buildingScaleDimensions[1] !== previousBuildingScaleDimensions[1] ||
            buildingScaleDimensions[2] !== previousBuildingScaleDimensions[2]
        ) &&
            (
                buildingScaleDimensions[0] !== 0 &&
                buildingScaleDimensions[1] !== 0 &&
                buildingScaleDimensions[2] !== 0
            )
        ) {

            const b = { ...buildingProps };

            const updatedLength = buildingProps.buildingLength !== buildingScaleDimensions[0];
            const updatedHeight = buildingProps.buildingHeight !== buildingScaleDimensions[1];
            const updatedWidth = buildingProps.buildingWidth !== buildingScaleDimensions[2];

            if (updatedLength) {
                b.buildingLength = ServiceManager.utilityService.keepInBounds(buildingScaleDimensions[0], minLength, maxLength);
            }
            if (updatedHeight) {
                b.buildingHeight = ServiceManager.utilityService.keepInBounds(buildingScaleDimensions[1], minHeight, maxHeight);
            }
            if (updatedWidth) {
                b.buildingWidth = ServiceManager.utilityService.keepInBounds(buildingScaleDimensions[2], minWidth, maxWidth)
            }

            if ((updatedLength && !updatedHeight) || (updatedLength && updatedHeight)) {
                recomputeColumns(true, b);
            } else {
                recomputeColumns(false, b);
            }
        }
    }, [buildingScaleDimensions, previousBuildingScaleDimensions, buildingProps, recomputeColumns]);


    const getRealLength = (bp: PropsCorridor) => {
        let result = 0;
        result += CorridorService.getBodySizeForComputationBasedOnColumnsAmount(bp) * config.corridorBaySize;
        if (bp.selectedHeadType !== headType.none) {
            result += getHeadTypesModel().find(s => s.type === bp.selectedHeadType).bays * config.corridorBaySize * 2;
        }
        setBuildingLengthUpdatedByColumns(true);
        return result;
    }

    const handleChange = (e: React.FocusEvent<HTMLInputElement, Element>, type: 'width' | 'length' | 'height' | 'customPlinthWidth' | 'customPlinthHeight') => {
        const bp = { ...buildingProps };
        const value = +(e.target as any).value as number;
        switch (type) {
            case 'width':
                bp.buildingWidth = ServiceManager.utilityService.keepInBounds(value, minWidth, maxWidth);
                (e.target as HTMLInputElement).value = bp.buildingWidth.toString();
                setBuildingProps({ ...bp });
                buildingChange({ ...bp }, currentBuilding, onChangeBuilding);
                break;
            case 'length':
                bp.buildingLength = ServiceManager.utilityService.keepInBounds(value, minLength, maxLength);
                (e.target as HTMLInputElement).value = bp.buildingLength.toString();
                // this is done to avoid circular computations. When amount of columns get updated.
                if (!buildingLengthUpdatedByColumns) {
                    recomputeColumns(true, bp);;
                } else {
                    setBuildingLengthUpdatedByColumns(false);
                }
                break;
            case 'height':
                bp.buildingHeight = ServiceManager.utilityService.keepInBounds(value, minHeight, maxHeight);

                (e.target as HTMLInputElement).value = bp.buildingHeight.toString();
                if (bp.buildingHeight > config.highRiseLimit || CorridorService.getTotalFloorsForElevator(bp) > 4) {
                    const currentHead = getHeadTypesModel().find(head => head.type === bp.selectedHeadType);
                    if (!currentHead.highrise) {
                        bp.selectedHeadType = defaultHead;
                    }
                }

                recomputeColumns(false, bp);
                break;
            case 'customPlinthHeight':
                bp.customPlinthHeight = value;
                recomputeColumns(false, bp);
                break;
            case 'customPlinthWidth':
                bp.customPlinthWidth = value;
                recomputeColumns(false, bp);
                break;
            default:
                alert('unknown value type provided');
                break;
        }
    }

    const handleSelect = (e: React.FormEvent<HTMLSelectElement | HTMLInputElement> | SelectChangeEvent<any>, type: 'moosPlazaType' | 'plinthType' | 'moosPlazaAlignment' | 'moosPlazaPosition' | 'selectedHeadType' | 'plinthFloors') => {
        const bp = { ...buildingProps };
        const value = (e.target as any).value as any;
        switch (type) {
            case 'moosPlazaType':
                bp.moosPlazaType = value;
                if (bp.moosPlazaType === columnType.none) {
                    bp.moosPlazaAlignment = alignment.center;
                }
                recomputeColumns(false, bp);
                break;
            case 'plinthType':
                bp.plinthType = value;
                if (bp.plinthType === plinthType.Custom) {
                    bp.plinthFloors = 1;
                    if (!bp.customPlinthWidth) {
                        bp.customPlinthWidth = 10;
                    }
                    if (!bp.customPlinthHeight) {
                        bp.customPlinthHeight = 6.25;
                    }
                } else {
                    bp.plinthFloors = 2;
                    bp.customPlinthWidth = 0;
                    bp.customPlinthHeight = 0;
                }
                recomputeColumns(false, bp);
                break;
            case 'moosPlazaAlignment':
                bp.moosPlazaAlignment = value;
                recomputeColumns(false, bp);
                break;
            case 'moosPlazaPosition':
                bp.moosPlazaPosition = value;
                recomputeColumns(false, bp);
                break;
            case 'selectedHeadType':
                bp.selectedHeadType = value;
                recomputeColumns(false, bp);
                break;
            case 'plinthFloors':
                bp.plinthFloors = +value;
                recomputeColumns(false, bp);
                break;
            default:
                alert('unknown value type provided');
                break;
        }
    }

    const handleColumnTypeChange = (increment: boolean, type: columnType) => {
        const bp = { ...buildingProps };
        switch (type) {
            case columnType.C_XS:
                bp.amount_C_XS_Columns = increment ? bp.amount_C_XS_Columns + 1 : bp.amount_C_XS_Columns - 1;
                break;
            case columnType.C_S1S2:
                bp.amount_C_S_Columns = increment ? bp.amount_C_S_Columns + 1 : bp.amount_C_S_Columns - 1;
                break;
            case columnType.C_L:
                bp.amount_C_L_Columns = increment ? bp.amount_C_L_Columns + 1 : bp.amount_C_L_Columns - 1;
                break;
            case columnType.C_M:
                bp.amount_C_M_Columns = increment ? bp.amount_C_M_Columns + 1 : bp.amount_C_M_Columns - 1;
                break;
            case columnType.C_XL:
                bp.amount_C_XL_Columns = increment ? bp.amount_C_XL_Columns + 1 : bp.amount_C_XL_Columns - 1;
                break;
            default:
                alert('unknown value type provided');
                break;
        }
        bp.buildingLength = getRealLength(bp);
        recomputeColumns(false, bp);
    }

    const getValueOfMoosPlazaBaysOptions = () => {
        return CorridorService.getColumnBays(buildingProps.moosPlazaType)
    }

    const updateCustomPlinth = () => {
        let bp = { ...buildingProps };
        if (bp.plinthType === plinthType.Custom) {
            bp.plinthType = plinthType.C;
            bp.plinthFloors = 2;
            bp.customPlinthWidth = 0;
            bp.customPlinthHeight = 0;
        } else {
            bp.plinthType = plinthType.Custom;
            bp.moosPlazaType = columnType.none;
            bp.plinthFloors = 1;
            if (!bp.customPlinthWidth) {
                bp.customPlinthWidth = 10;
            }
            if (!bp.customPlinthHeight) {
                bp.customPlinthHeight = config.corridordFloorHeight * 2;
            }
        }
        recomputeColumns(false, bp);
    }

    const updateNoneMoosPlaza = () => {
        let bp = { ...buildingProps };
        if (bp.moosPlazaType === columnType.none) {
            bp.moosPlazaType = columnType.V_P32C_C_M;
            setMoosPlazaBays(3);
        } else {
            bp.moosPlazaType = columnType.none;
            bp.moosPlazaAlignment = alignment.center;
        }
        recomputeColumns(false, bp);
    }

    const updateMoosPlazaType = (type: columnType) => {
        let bp = { ...buildingProps };
        bp.moosPlazaType = type;
        recomputeColumns(false, bp);
    }

    const updateNoneHead = () => {
        let bp = { ...buildingProps };
        if (bp.selectedHeadType === headType.none) {
            bp.selectedHeadType = headType._2C_XL;
        } else {
            bp.selectedHeadType = headType.none;
        }
        recomputeColumns(false, bp);
    }

    const getSelectFormTopOptions = (bays: number) => {
        const moptions = moosPlazaTopOptions.find(o => o.forMoosPlazaBays === bays);
        return moptions && moptions.options.some(o => o.type === buildingProps.moosPlazaType) && moptions.forMoosPlazaBays === moosPlazaBays &&
            <div className={buildingConfStyles.formControl}>
                <FormControl size="medium" fullWidth >
                    <InputLabel id={"moosplaza-top-selection-label"}>Woningen boven entree</InputLabel>
                    <Select
                        labelId={"moosplaza-top-selection-label"}
                        id={"moosplaza-top-selection-id"}
                        data-testid="moosplaza-top-selection-id"
                        value={buildingProps.moosPlazaType}
                        onChange={(event) => { updateMoosPlazaType(event.target.value as columnType) }}
                        label="Woningen boven entree"
                    >
                        {
                            moptions.options.map((bs, index) => {
                                return (
                                    <MenuItem data-testid={'moosplaza-top-selection-' + index} key={'moosplaza-top-selection-' + index} value={bs.type}>
                                        <div className={buildingConfStyles.selectDetails}>
                                            <div>
                                                <span className={buildingConfStyles.boldLabel}>{bs.name}</span>
                                                <span className={buildingConfStyles.smallExplanation}>
                                                    &nbsp;en {bs.subname}
                                                </span>
                                            </div>
                                            <div>
                                                {bs.unitDetails.map((s, index) => {
                                                    return <div key={'detail-' + index} className={buildingConfStyles.smallExplanation}>
                                                        {s.unit} unit - {s.details}
                                                    </div>
                                                })}
                                            </div>
                                        </div>
                                    </MenuItem>
                                )
                            })
                        }
                    </Select>
                </FormControl>
            </div>
    }


    const getSelectFormHeadOptions = () => {
        return headOptions[0].options && headOptions[0].options.length &&
            <div className={buildingConfStyles.formControl}>
                <FormControl id={`head-selection-control`} size="medium" fullWidth >
                    <InputLabel id={`head-selection-label`}>Kop</InputLabel>
                    <Select
                        labelId={`head-selection-label`}
                        id={`head-selection-id`}
                        data-testid={`head-selection-id`}
                        value={buildingProps.selectedHeadType}
                        onChange={(event) => { handleSelect(event, 'selectedHeadType') }}
                        label={'Kop type'}
                    >
                        {
                            headOptions[0].options.map((bs, index) => {
                                return (
                                    <MenuItem data-testid={`head-selection-${index}`} key={`head-selection-${index}`} value={bs.type}>
                                        <div className={buildingConfStyles.selectDetails}>
                                            <div>
                                                <span className={buildingConfStyles.boldLabel}>{bs.name}</span>
                                                {bs.subname &&
                                                    <span className={buildingConfStyles.smallExplanation}>
                                                        &nbsp;en {bs.subname}
                                                    </span>
                                                }
                                            </div>
                                            <div>
                                                {bs.unitDetails.map((s, index) => {
                                                    return <div key={'detail-' + index} className={buildingConfStyles.smallExplanation}>
                                                        {s.unit} unit - {s.details}
                                                    </div>
                                                })}
                                            </div>
                                        </div>
                                    </MenuItem>
                                )
                            })
                        }
                    </Select>
                </FormControl>
            </div>
    }

    return (
        <div data-testid="Corridor">
            {buildingProps && <>
                <div className="group">
                    <h2 className="greyed-out-text">Kolommen aanpassen</h2>
                    <div>
                        <div className={'dimensions ' + buildingConfStyles.columnNr}>
                            <NumberArrowSelector
                                label="XS"
                                min={0}
                                max={100}
                                value={buildingProps.amount_C_XS_Columns}
                                increment={() => handleColumnTypeChange(true, columnType.C_XS)}
                                decrement={() => handleColumnTypeChange(false, columnType.C_XS)}
                            />
                            <NumberArrowSelector
                                label="S"
                                min={0}
                                max={100}
                                value={buildingProps.amount_C_S_Columns}
                                increment={() => handleColumnTypeChange(true, columnType.C_S1S2)}
                                decrement={() => handleColumnTypeChange(false, columnType.C_S1S2)}
                            />
                            <NumberArrowSelector
                                label="M"
                                min={0}
                                max={100}
                                value={buildingProps.amount_C_M_Columns}
                                increment={() => handleColumnTypeChange(true, columnType.C_M)}
                                decrement={() => handleColumnTypeChange(false, columnType.C_M)}
                            />
                            <NumberArrowSelector
                                label="L"
                                min={0}
                                max={100}
                                value={buildingProps.amount_C_L_Columns}
                                increment={() => handleColumnTypeChange(true, columnType.C_L)}
                                decrement={() => handleColumnTypeChange(false, columnType.C_L)}
                            />
                            <NumberArrowSelector
                                label="XL"
                                min={0}
                                max={100}
                                value={buildingProps.amount_C_XL_Columns}
                                increment={() => handleColumnTypeChange(true, columnType.C_XL)}
                                decrement={() => handleColumnTypeChange(false, columnType.C_XL)}
                            />
                        </div>
                        <div className="extras">
                        </div>
                    </div>
                </div>
                <hr></hr>
                <div className="group">
                    <h2 className="greyed-out-text">Plint indelen</h2>
                    <FormGroup>
                        <FormControlLabel control={<Switch
                            checked={buildingProps.plinthType === plinthType.Custom}
                            onChange={updateCustomPlinth}
                        />} label="Maatwerk plint" />
                        <FormControlLabel disabled={buildingProps.plinthType === plinthType.Custom} control={<Switch
                            checked={buildingProps.moosPlazaType !== columnType.none}
                            onChange={updateNoneMoosPlaza}
                        />} label="Entree" />
                    </FormGroup>
                    {
                        buildingProps.moosPlazaType !== columnType.none && moosPlazaBays !== undefined && moosPlazaTopOptions.length > 0 &&
                        <>
                            {
                                moosPlazaBaysOptions.length > 0 &&
                                <div className={buildingConfStyles.formControl}>
                                    <FormControl size="medium" fullWidth>
                                        <InputLabel id={"moosplaza-bays-selection-label"}>Entree breedte</InputLabel>
                                        <Select
                                            labelId={"moosplaza-bays-selection-label"}
                                            id={"moosplaza-bays-selection-id"}
                                            data-testid="moosplaza-bays-selection-id"
                                            value={getValueOfMoosPlazaBaysOptions()}
                                            onChange={(event) => {
                                                setMoosPlazaBays(+event.target.value)
                                            }}
                                            label="Entree breedte"
                                        >
                                            {
                                                moosPlazaBaysOptions.map((bs, index) => {
                                                    return (
                                                        <MenuItem data-testid={'moosplaza-bays-selection-' + index} key={'moosplaza-bays-selection-' + index} value={bs.bays}>
                                                            <div>
                                                                <span className={buildingConfStyles.boldLabel}>{bs.bays} beuken</span> - {bs.squareMeters} ㎡
                                                            </div>
                                                        </MenuItem>
                                                    )
                                                })
                                            }
                                        </Select>
                                    </FormControl>
                                </div>
                            }
                            {
                                getSelectFormTopOptions(moosPlazaBays)
                            }
                            <div className={buildingConfStyles.formControl}>
                                <FormControl size="medium" fullWidth>
                                    <InputLabel id={"moosplaza-alignment-selection-label"}>Positie</InputLabel>
                                    <Select
                                        labelId={"moosplaza-alignment-selection-label"}
                                        id={"moosplaza-alignment-selection-id"}
                                        data-testid="moosplaza-alignment-selection-id"
                                        value={buildingProps.moosPlazaAlignment}
                                        onChange={(event) => {
                                            handleSelect(event, 'moosPlazaAlignment')
                                        }}
                                        label="Positie"
                                    >
                                        <MenuItem data-testid={'moosplaza-alignment-selection-' + 0} value={alignment.left}>
                                            Links
                                        </MenuItem>
                                        <MenuItem data-testid={'moosplaza-alignment-selection-' + 0} value={alignment.center}>
                                            Midden
                                        </MenuItem>
                                        <MenuItem data-testid={'moosplaza-alignment-selection-' + 0} value={alignment.right}>
                                            Rechts
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </div>
                            <div className={buildingConfStyles.formControl}>
                                <FormControl size="medium" fullWidth>
                                    <InputLabel id={"moosplaza-body-selection-label"}>Deel</InputLabel>
                                    <Select
                                        labelId={"moosplaza-body-selection-label"}
                                        id={"moosplaza-body-selection-id"}
                                        data-testid="moosplaza-body-selection-id"
                                        value={buildingProps.moosPlazaPosition}
                                        onChange={(event) => {
                                            handleSelect(event, 'moosPlazaPosition')
                                        }}
                                        label="Deel"
                                    >
                                        <MenuItem data-testid={'moosplaza-body-selection-' + 0} value={bodiesEnum.A}>
                                            A
                                        </MenuItem>
                                        <MenuItem data-testid={'moosplaza-body-selection-' + 0} value={bodiesEnum.B}>
                                            B
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </div>
                        </>
                    }
                    <div className={buildingConfStyles.formControl}>
                        {
                            buildingProps.plinthType === plinthType.Custom ?
                                <dl>
                                    <dt>
                                        Plint verdiepingen</dt>
                                    <dd>
                                        {buildingProps.plinthFloors}
                                    </dd>
                                </dl>
                                :
                                <div className={buildingConfStyles.formControl}>
                                    <FormControl size="medium" fullWidth>
                                        <InputLabel id={"plint-floors-selection-label"}>Plint verdiepingen</InputLabel>
                                        <Select
                                            labelId={"plint-floors-selection-label"}
                                            id={"plint-floors-selection-id"}
                                            data-testid="plint-floors-selection-id"
                                            value={buildingProps.plinthFloors}
                                            onChange={(e) => {
                                                handleSelect(e as any, 'plinthFloors')
                                            }}
                                            label="Plint verdiepingen"
                                        >
                                            <MenuItem data-testid={'alignment-selection-' + 0} value={1}>
                                                1
                                            </MenuItem>
                                            <MenuItem data-testid={'alignment-selection-' + 0} value={2}>
                                                2
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                </div>
                        }
                    </div>
                    {buildingProps.plinthType === plinthType.Custom &&
                        <div >
                            <label>
                                Custom Plint Breedte (m)
                                <input type="number" value={buildingProps.customPlinthWidth}
                                    onChange={(e) => { setBuildingProps({ ...buildingProps, customPlinthWidth: +e.target.value }) }}
                                    onBlur={(e) => handleChange(e, 'customPlinthWidth')} placeholder="breedte"></input>
                            </label>
                            <label>
                                Custom Plint Hoogte (m)
                                <input type="number" value={buildingProps.customPlinthHeight}
                                    onChange={(e) => { setBuildingProps({ ...buildingProps, customPlinthHeight: +e.target.value }) }}
                                    onBlur={(e) => handleChange(e, 'customPlinthHeight')} placeholder="hoogte"></input>
                            </label>
                        </div>
                    }
                </div>
                <hr></hr>
                <div className="group">
                    <div className={buildingConfStyles.formControl}>
                        <FormGroup>

                            <FormControlLabel control={<Switch
                                checked={buildingProps.selectedHeadType !== headType.none}
                                onChange={updateNoneHead}
                            />} label="Koppen" />
                        </FormGroup>
                        {
                            buildingProps.selectedHeadType !== headType.none && getSelectFormHeadOptions()
                        }
                    </div>
                </div>
            </>}
        </div>
    )
};

export default Corridor;
